import { RequestOptions } from 'bernie-graphql';
import { FetchOptions } from 'bernie-core';
import { SearchCriteriaStore } from 'src/stores';
import { KeyedObject } from 'bernie-core/dist/source';
import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getParam(query: any, paramKey: string): string[] {
  return Object.keys(query).filter(key => paramKey === key);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateRequestOptions(request: any, isServer: boolean, pageId: string): RequestOptions {
  const { traceInfo, deviceId } = request.context;

  const options: RequestOptions = {
    pageId: pageId,
    extraHeaders: { 'Device-User-Agent-ID': deviceId }
  };

  if (isServer) {
    options.traceId = (traceInfo && traceInfo['Trace-ID']) || null;
  }

  return options;
}

export function needSearchFis(options: FetchOptions, forceFetch: boolean): boolean {
  const { query } = options;
  //bernie has these hard-coded, so we need to do the same for now
  const dialogRouteKeys = ['pwaDialog', 'pwaDialogNested', 'pwaOverlay', 'notificationCenterDeleteDialog'];
  const { prevQuery, ...currentQuery } = query;

  const hashKeys = ['#main_content'];
  const prevParamsDialogs = Object.keys(prevQuery).filter(key => dialogRouteKeys.includes(key) && prevQuery[key]);
  const currentParamsDialogs = Object.keys(currentQuery).filter(
    key => dialogRouteKeys.includes(key) && currentQuery[key]
  );

  const dialogOpen = currentQuery['pwaDialog'] && currentQuery['pwaDialog'] !== '';
  const nestedDialogOpen = currentQuery['pwaDialogNested'] && currentQuery['pwaDialogNested'] !== '';
  const notificationCenterDeleteDialogOpen =
    currentQuery['notificationCenterDeleteDialog'] && currentQuery['notificationCenterDeleteDialog'] !== '';

  const isClosingDialog = currentParamsDialogs.length < prevParamsDialogs.length;

  const fetchTriggeredByDialog =
    isClosingDialog || dialogOpen || nestedDialogOpen || notificationCenterDeleteDialogOpen;
  const isHashRoute = hashKeys.indexOf(window.location.hash) > -1;
  if (isHashRoute) {
    return false;
  }

  return !fetchTriggeredByDialog || (isClosingDialog && forceFetch);
}

export function needSearchFsr(options: FetchOptions, searchCriteriaStore?: SearchCriteriaStore): boolean {
  const { query } = options;

  //bernie has these hard-coded, so we need to do the same for now
  const dialogRouteKeys = ['pwaDialog', 'pwaDialogNested', 'pwaOverlay', 'notificationCenterDeleteDialog'];
  const hashKeys = ['#main_content'];
  const { prevQuery, ...currentQuery } = query;
  const prevParamsDialogs = Object.keys(prevQuery).filter(key => dialogRouteKeys.includes(key));
  const currentParamsDialogs = Object.keys(currentQuery).filter(key => dialogRouteKeys.includes(key));

  const isClosingDialog = currentParamsDialogs.length < prevParamsDialogs.length;
  const dialogOpen = currentQuery['pwaDialog'] && currentQuery['pwaDialog'] !== '';
  const nestedDialogOpen = currentQuery['pwaDialogNested'] && currentQuery['pwaDialogNested'] !== '';
  const notificationCenterDeleteDialogOpen =
    currentQuery['notificationCenterDeleteDialog'] && currentQuery['notificationCenterDeleteDialog'] !== '';

  const fetchTriggeredByDialog =
    dialogOpen || nestedDialogOpen || isClosingDialog || notificationCenterDeleteDialogOpen;
  const isHashRoute = hashKeys.indexOf(window.location.hash) > -1;

  if (!fetchTriggeredByDialog) {
    searchCriteriaStore.setCriteriaFromUrl(options);
  }

  if (isHashRoute) {
    return false;
  }

  return !fetchTriggeredByDialog || (isClosingDialog && searchCriteriaStore.forceFetch);
}

function needSearchBasedOnIdentifier(query: KeyedObject, identifierKey: string, jcidKey: string): boolean {
  if (query && query.journeyContinuationId && query.identifier) {
    const { prevQuery, ...currentQuery } = query;
    const prevParamIdentifier = getParam(prevQuery, identifierKey);
    const currentParamIdentifier = getParam(currentQuery, identifierKey);
    const prevParamJcid = getParam(prevQuery, jcidKey);
    const currentParamJcid = getParam(currentQuery, jcidKey);
    const withIdentifierCall = currentParamIdentifier.length >= prevParamIdentifier.length;
    const withJcidCall = currentParamJcid.length > prevParamJcid.length;

    return !withIdentifierCall || withJcidCall;
  }

  return true;
}

function needSearchBasedOnAncillaryId(query: KeyedObject, ancillaryId: string, jcidKey: string): boolean {
  if (query && query.journeyContinuationId) {
    const { prevQuery, ...currentQuery } = query;
    const currentParamAncillaryId = getParam(currentQuery, ancillaryId);
    const prevParamAncillaryId = getParam(prevQuery, ancillaryId);
    const prevParamJcid = getParam(prevQuery, jcidKey);
    const currentParamJcid = getParam(currentQuery, jcidKey);
    const withJcidCall = currentParamJcid.length > prevParamJcid.length;

    return withJcidCall || !(currentParamAncillaryId.length > 0 || prevParamAncillaryId.length > 0);
  }

  return true;
}

export function needSearchBasisOnInfoKeys(query: KeyedObject): boolean {
  const identifierKey = 'identifier';
  const jcidKey = 'journeyContinuationId';
  const ancillaryId = 'ancillaryId';
  let fetchTriggeredByInfoKeys = true;
  fetchTriggeredByInfoKeys = needSearchBasedOnIdentifier(query, identifierKey, jcidKey);
  fetchTriggeredByInfoKeys = fetchTriggeredByInfoKeys && needSearchBasedOnAncillaryId(query, ancillaryId, jcidKey);

  return fetchTriggeredByInfoKeys;
}

export async function isDomesticJourney(
  options: FetchOptions,
  origin: string,
  destination: string,
  logger?: ClientLogger
): Promise<void | string[]> {
  const locale = `?locale=${options.request.context.locale}`;
  let typeaheadUrl = '/api/v4/typeahead/';

  if (options.isServer) {
    typeaheadUrl = `https://www.expedia.com${typeaheadUrl}`;
  }

  return await Promise.all([fetch(typeaheadUrl + origin + locale), fetch(typeaheadUrl + destination + locale)])
    .then(responses => Promise.all(responses.map(res => res.json())))
    .then(originDestinations => {
      return originDestinations;
    })
    .catch(error => {
      logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FETCH_TYPEAHEAD'), error);

      return [];
    });
}

export function setIsDomestic(ODs) {
  if (ODs?.every(element => element.sr?.length)) {
    return ODs.length > 1 ? ODs.filter(OD => OD.sr[0].hierarchyInfo?.country?.isoCode3 === 'USA').length === 2 : false;
  }

  return false;
}

export const isFlightsPredictiveSearch = (options: FetchOptions) => options.query?.predictive === 'true';

export const hasSortOrFilters = (query: KeyedObject) =>
  query?.filters || query?.sortOrder || query?.sortType || query?.journeysContinuationId ? true : false;
